// Assign invoice-level references (found anywhere in the KSeF invoice — footer,
// annotations, order fields) to specific invoice lines.
//
// The reference is often NOT in the line text. `invoices.external_references` carries the
// canonical `IRM1501…` tokens extracted during KSeF verification. Here we attach each to
// the line it most likely bills, so the existing P1 reference matcher (which scans the
// line's own `reference_number`) can link it:
//
//   - single reference, single billable line → attach it (ref-trusting), even if no priced
//     candidate is present — the reference names the transaction unambiguously;
//   - otherwise → greedy assignment by CLOSEST net amount (each reference used once, each
//     line receives at most one), so "multiple references not tied to lines" resolve to the
//     line they best match.
//
// Lines that already carry their own reference are never overwritten.

import { isZero } from '../../../calc.ts';
import type { MatchCandidate, MatchLineItem } from './types.ts';

/** One reference→line attachment. `lineId` is the {@link MatchLineItem.id} (the ordinal). */
export interface ReferenceAssignment {
  lineId: number;
  reference_number: string;
}

/**
 * Attach `externalReferences` to `items` (mutates `items[].reference_number`) and return
 * the attachments made. `candidates` supplies each reference's expected net (the vendor
 * transaction carrying it) for the amount-based assignment.
 */
export function assignReferencesToLines(
  items: MatchLineItem[],
  candidates: MatchCandidate[],
  externalReferences: string[] | null | undefined,
): ReferenceAssignment[] {
  const refs = dedupe(externalReferences ?? []);
  if (refs.length === 0) return [];

  // reference → the net of the (first priced) candidate carrying it.
  const netByRef = new Map<string, number>();
  for (const ref of refs) {
    const cand = candidates.find(c => c.reference_number === ref && c.pricing?.net_amount != null);
    if (cand) netByRef.set(ref, Math.abs(cand.pricing.net_amount));
  }

  const eligible = items.filter(li => !li.reference_number && !isZero(li.net_amount ?? 0));
  const takenLines = new Set<number>();
  const out: ReferenceAssignment[] = [];

  // Greedy: attach each reference with a known candidate net to the closest-net free line.
  for (const ref of refs) {
    const refNet = netByRef.get(ref);
    if (refNet === undefined) continue;
    let best: MatchLineItem | null = null;
    let bestDist = Infinity;
    for (const li of eligible) {
      if (takenLines.has(li.id)) continue;
      const dist = Math.abs(Math.abs(li.net_amount ?? 0) - refNet);
      if (dist < bestDist) { bestDist = dist; best = li; }
    }
    if (best) {
      best.reference_number = ref;
      takenLines.add(best.id);
      out.push({ lineId: best.id, reference_number: ref });
    }
  }

  // Fallback: a lone reference on a single billable line attaches even without a priced
  // candidate (ref-trusting) — the reference names the transaction unambiguously.
  if (out.length === 0 && refs.length === 1 && eligible.length === 1) {
    eligible[0].reference_number = refs[0];
    out.push({ lineId: eligible[0].id, reference_number: refs[0] });
  }

  return out;
}

function dedupe(refs: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of refs) {
    if (r && !seen.has(r)) { seen.add(r); out.push(r); }
  }
  return out;
}
